/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import {
  createIncident,
  getMyIncidents,
} from "../api/incidents";
import { getTrialStatus, onboardingErrorMessage } from "../api/onboarding";
import {
  entitledIncidentWorkflows,
  evidenceCategoriesForWorkflows,
} from "../utils/incidentWorkflowEntitlements";

export default function MyIncidents() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [workflowType, setWorkflowType] = useState("");
  const [severity, setSeverity] = useState("Medium");
  const [evidenceType, setEvidenceType] = useState("");
  const [customEvidenceType, setCustomEvidenceType] = useState("");
  const [trial, setTrial] = useState(null);
  const [trialLoading, setTrialLoading] = useState(true);
  const [trialError, setTrialError] = useState("");
  const entitledWorkflows = entitledIncidentWorkflows(trial);
  const evidenceTypeOptions = evidenceCategoriesForWorkflows(entitledWorkflows);
  const canCreateIncident = !trialLoading
    && !trialError
    && entitledWorkflows.length > 0
    && evidenceTypeOptions.length > 0
    && trial?.can_start_live_incident;

  const workflowGuidanceChecklist = {
    "Design": [
      "Part, product, or system affected",
      "Design change or concern",
      "Reason for the change",
      "Engineering question or decision needed",
      "Photos, drawings, or review evidence attached",
    ],

    "Incoming Material Inspection": [
      "Supplier name",
      "Material / part number",
      "PO, lot, batch, or heat number",
      "Quantity received and quantity affected",
      "Defect or mismatch observed",
      "Disposition: accept, hold, reject, or escalate",
    ],

    "Supplier Quality Containment": [
      "Supplier name",
      "Affected material or part",
      "Defect, risk, or non-conformance",
      "Quantity impacted",
      "Containment action taken",
      "Next owner or follow-up needed",
    ],

    "Production Quality Escalation": [
      "Production line or workstation",
      "Part, batch, or job affected",
      "Issue observed",
      "Quantity impacted",
      "Immediate action taken",
      "Who was notified",
    ],

    "Packaging / Shipping Damage": [
      "Shipment, PO, or delivery reference",
      "Packaging condition",
      "Items damaged",
      "Quantity affected",
      "Carrier, supplier, or customer if known",
      "Photos attached",
    ],

    "Equipment / Downtime Incident": [
      "Machine or equipment name",
      "Location or production line",
      "Failure symptom",
      "Downtime start time",
      "Production impact",
      "Maintenance action needed",
    ],

    "Other": [
      "What happened",
      "Where it happened",
      "Who or what was involved",
      "Why it matters",
      "Action needed next",
    ],
    "Quality Incident / Nonconformance (NCR)": [
      "Part, material, product, or process affected",
      "Defect or nonconformance observed",
      "Quantity or scope impacted",
      "Immediate containment or disposition",
      "Supplier, line, lot, PO, or shipment reference",
      "Owner and next action needed",
    ],
  };

  const severityOptions = ["Low", "Medium", "High", "Containment Required"];

  useEffect(() => {
    loadIncidents();
    getTrialStatus()
      .then((summary) => {
        setTrial(summary);
        const workflows = entitledIncidentWorkflows(summary);
        setWorkflowType(workflows[0]?.value || "");
        setEvidenceType(evidenceCategoriesForWorkflows(workflows)[0] || "");
        setTrialError("");
      })
      .catch((err) => {
        setTrialError(onboardingErrorMessage(err, "Workflow access could not be loaded."));
      })
      .finally(() => setTrialLoading(false));
  }, []);

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setShowCreatePanel(true);
      setNewTitle(searchParams.get("title") || "");
    }
  }, [searchParams]);

  async function loadIncidents() {
    try {
      setLoading(true);

      const data = await getMyIncidents();

      setIncidents(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateIncident() {
  console.log("New Incident clicked");

  try {
    setCreating(true);

    if (!canCreateIncident || !workflowType) {
      throw new Error(trialError || "No incident workflow is available for this workspace.");
    }

    const resolvedWorkflowType = workflowType;
    
    const resolvedEvidenceType =
      evidenceType === "Other" && customEvidenceType.trim()
        ? customEvidenceType.trim()
        : evidenceType;
      
    const incident = await createIncident({
      title: newTitle.trim() || resolvedWorkflowType,
      workflow_type: resolvedWorkflowType,
      severity,
      evidence_type: resolvedEvidenceType,
    });

    console.log("Created incident:", incident);

    toast.success("Incident workspace created");

    navigate(`/secure/incidents/${incident.id}`);
  } catch (err) {
    console.error("Create incident failed:", err);
    alert(onboardingErrorMessage(err, "Failed to create incident"));
  } finally {
    setCreating(false);
  }
}

  function renderIncidentCard(incident) {
    return (
      <div
        key={incident.id}
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
              Incident
            </div>

            <h3 className="font-bold text-slate-900 dark:text-white">
              {incident.incident_number}
            </h3>

            <p className="text-sm text-slate-500 mt-2">
              {incident.title || "Untitled Incident"}
            </p>
          </div>

          <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
            {formatStatus(incident.status)}
          </span>
        </div>

        <div className="mt-4 text-xs text-slate-500">
          Created:{" "}
          {new Date(incident.created_at).toLocaleString()}
        </div>

        <button
          onClick={() =>
            navigate(`/secure/incidents/${incident.id}`)
          }
          className="mt-5 w-full bg-primary text-white rounded-lg py-2 text-sm font-semibold hover:opacity-90 transition"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            My Incident Workspace
          </h1>

          <p className="text-slate-500 mt-1">
            Capture operational incidents and attach evidence over time.
          </p>
        </div>

        <button
          onClick={() => setShowCreatePanel(true)}
          disabled={creating || !canCreateIncident}
          className="bg-primary text-white px-5 py-3 rounded-xl font-semibold shadow hover:opacity-90 transition disabled:opacity-50"
        >
          {creating ? "Creating..." : "+ New Incident"}
        </button>
      </div>

      {trial?.status === "trialing" && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-950">
          <div><strong>Quality Incident trial</strong><span className="ml-2 text-blue-800/70">One NCR workflow · {trial.live_incidents.remaining} of {trial.live_incidents.limit} live incidents remaining</span></div>
          <button onClick={() => navigate("/onboarding/welcome")} className="font-bold text-blue-700 hover:text-blue-900">Sample &amp; usage</button>
        </div>
      )}

      {showCreatePanel && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h2 className="font-bold text-slate-900 dark:text-white">
                Start a Guided Incident Capture
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Add just enough context so this is easy to find later. You can record voice and attach evidence next.
              </p>
            </div>

            <button
              onClick={() => setShowCreatePanel(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Short Title
              </label>
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Supplier sent wrong quantity"
                className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-2 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Workflow Type
              </label>
              {trialLoading ? (
                <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">Loading workflow access…</div>
              ) : trialError || entitledWorkflows.length === 0 ? (
                <div className="mt-1 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{trialError || "No workflow is configured for this workspace."}</div>
              ) : entitledWorkflows.length === 1 ? (
                <div className="mt-1 rounded-lg border border-blue-200 bg-blue-50 p-3"><div className="text-sm font-semibold text-blue-950">{entitledWorkflows[0].label}</div><div className="mt-1 text-xs text-blue-800/70">Included with this workspace</div></div>
              ) : (
                <select
                  value={workflowType}
                  onChange={(e) => setWorkflowType(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-2 text-sm"
                >
                  {entitledWorkflows.map((workflow) => (
                    <option key={workflow.key} value={workflow.value}>{workflow.label}</option>
                  ))}
                </select>
              )}
              <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-3">
                <div className="mb-2 text-xs font-semibold text-blue-900">
                  What to mention in your recording
                </div>

                <ul className="space-y-1 text-xs text-blue-900">
                  {(workflowGuidanceChecklist[workflowType] ||
                    workflowGuidanceChecklist.Other
                  ).map((item) => (
                    <li key={item} className="flex gap-2">
                      <span>✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Severity
              </label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-2 text-sm"
              >
                {severityOptions.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Evidence Category
              </label>
              {evidenceTypeOptions.length > 0 ? <><select
                  value={evidenceType}
                  onChange={(e) => setEvidenceType(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-2 text-sm"
                >
                  {evidenceTypeOptions.map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select><p className="mt-1 text-xs text-slate-500">Choose the primary kind of evidence supporting this nonconformance.</p></> : <div className="mt-1 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">No evidence categories are configured for this workflow.</div>}
            </div>
            {evidenceType === "Other" && (
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Other Evidence Category
                </label>

                <input
                  value={customEvidenceType}
                  onChange={(e) => setCustomEvidenceType(e.target.value)}
                  placeholder="Describe evidence type"
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-2 text-sm"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowCreatePanel(false)}
              className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-600 dark:text-slate-300"
            >
              Cancel
            </button>

            <button
              onClick={handleCreateIncident}
              disabled={creating || !canCreateIncident}
              className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-bold disabled:opacity-50"
            >
              {creating ? "Creating..." : "Continue to Voice Capture"}
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 text-center text-slate-500">
          Loading incidents...
        </div>
      )}

      {/* Empty */}
      {!loading && incidents.length === 0 && (
        <div className="bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-12 text-center">
          <div className="text-slate-500 mb-4">
            No incidents yet
          </div>

          <button
            onClick={() => setShowCreatePanel(true)}
            disabled={creating || !canCreateIncident}
            className="bg-primary text-white px-5 py-3 rounded-xl font-semibold disabled:opacity-50"
          >
            Create First Incident
          </button>
        </div>
      )}

      {/* Incident Grid */}
      {!loading && incidents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {incidents.map(renderIncidentCard)}
        </div>
      )}
    </div>
  );
}

function formatStatus(status) {
  if (!status) return "Unknown";

  return String(status)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
